import { FC } from "react";
import { Button, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FieldValues, useForm, UseFormReturn } from "react-hook-form";
import { AuthService } from "@service/auth.service";
import ValidationError from "@components/ValidationError/ValidationError";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setEmail } from "@store/signUpSlice";
import PasswordField, {
  PasswordFieldFormType,
} from "@components/PasswordField/PasswordField";
import { setMessage } from "@store/snackbarSlice";
import PublicForm from "@components/PublicForm";
import CustomLabelInput from "@components/CustomLabelInput";
import SocialSignIn from "@components/Onboarding/SocialSignIn";
import TermsOfService from "@components/TermsOfService";

const authService = AuthService.instance;

interface SignUpFormType extends PasswordFieldFormType {
  email: string;
  password: string;
}

const SignUpForm: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const form = useForm<SignUpFormType>({
    mode: "onChange",
    criteriaMode: "all",
  });

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { isSubmitting, errors },
  } = form;

  const signUp = async ({ email, password }: FieldValues): Promise<void> => {
    if (!email || !password) {
      return;
    }
    try {
      await authService.signUp(email, password);
      dispatch(setEmail(email));
      navigate("/verify-email");
    } catch (e) {
      const type =
        (e as any)?.name === "UsernameExistsException"
          ? "userNameExists"
          : "unknown";
      dispatch(setMessage({ content: t(`errors.${type}`), severity: "error" }));
    }
  };

  return (
    <PublicForm title={t("signUp.title")} subtitle={t("signUp.subtitle")}>
      <SocialSignIn onSignInClick={clearErrors} />

      <PublicForm.PublicFormContainer
        onSubmit={(e) => {
          clearErrors();
          return handleSubmit(signUp)(e);
        }}
      >
        <CustomLabelInput label={t("signUp.emailInput.label")} id="email">
          <TextField
            {...register("email", {
              required: true,
            })}
            error={!!errors?.email}
            type="email"
            size="small"
            variant="outlined"
            fullWidth
            placeholder={t("signUp.emailInput.placeholder")}
          />
        </CustomLabelInput>

        <PasswordField
          form={form as unknown as UseFormReturn<PasswordFieldFormType>}
        />

        {errors.root && (
          <ValidationError>{t(`errors.${errors.root.type}`)}</ValidationError>
        )}

        <PublicForm.PublicFormActionContainer>
          <Button
            name="signup-button"
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            size="large"
            fullWidth
          >
            {t("signUp.continueButton")}
          </Button>
        </PublicForm.PublicFormActionContainer>
      </PublicForm.PublicFormContainer>

      <PublicForm.PublicFormFooter>
        <span>{t("signUp.accountExistsLabel")}</span>
        <PublicForm.PublicFormLink to="/sign-in">
          {t("signUp.signInLink")}
        </PublicForm.PublicFormLink>

        <PublicForm.PublicFormTos>
          <TermsOfService />
        </PublicForm.PublicFormTos>
      </PublicForm.PublicFormFooter>
    </PublicForm>
  );
};

export default SignUpForm;
